/* Componente de anexos: clip + drag-and-drop + paste + thumbnails */
import React, { useRef, useCallback, useState, forwardRef } from 'react';
import type { AttachmentFile } from '../types';

/** Tipos MIME aceitos */
const TIPOS_ACEITOS = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
] as const;

type TipoMime = (typeof TIPOS_ACEITOS)[number];

const MAX_TAMANHO_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_ARQUIVOS = 3;

interface AttachmentClipProps {
  /** Lista de anexos atuais */
  attachments: AttachmentFile[];
  /** Callback para atualizar a lista de anexos */
  onAttachmentsChange: (files: AttachmentFile[]) => void;
  /** Função de tradução */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Se true, desabilita o botão (ex: durante loading do chat) */
  disabled?: boolean;
}

/** Gera ID único para o arquivo */
function gerarId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Lê arquivo como base64 */
function lerArquivoBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove o prefixo "data:image/png;base64," — backend quer só o dado
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Componente de clip para anexar arquivos ao chat.
 * Suporta: click no botão, drag-and-drop, Ctrl+V de imagem da área de transferência.
 *
 * Regras:
 * - Máximo 5 MB por arquivo
 * - Máximo 3 arquivos simultâneos
 * - Tipos aceitos: image/* e application/pdf
 */
export const AttachmentClip = forwardRef<HTMLDivElement, AttachmentClipProps>(
  ({ attachments, onAttachmentsChange, t, disabled = false }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    /** Processa lista de File e adiciona à lista de anexos */
    const processarArquivos = useCallback(
      async (files: File[]) => {
        setErro(null);

        const vagasRestantes = MAX_ARQUIVOS - attachments.length;
        if (vagasRestantes <= 0) {
          setErro(t('chat.attach.maxFiles', { max: MAX_ARQUIVOS }));
          return;
        }

        const arquivosParaProcessar = files.slice(0, vagasRestantes);
        const novosAnexos: AttachmentFile[] = [];

        for (const file of arquivosParaProcessar) {
          // Valida tipo
          if (!TIPOS_ACEITOS.includes(file.type as TipoMime)) {
            setErro(t('chat.attach.invalidType'));
            continue;
          }

          // Valida tamanho
          if (file.size > MAX_TAMANHO_BYTES) {
            setErro(
              t('chat.attach.maxSize', { name: file.name, max: '5MB' }),
            );
            continue;
          }

          try {
            const base64 = await lerArquivoBase64(file);
            novosAnexos.push({
              id: gerarId(),
              filename: file.name,
              mediaType: file.type as TipoMime,
              base64,
              sizeBytes: file.size,
            });
          } catch {
            setErro(t('errors.generic'));
          }
        }

        if (novosAnexos.length > 0) {
          onAttachmentsChange([...attachments, ...novosAnexos]);
        }
      },
      [attachments, onAttachmentsChange, t],
    );

    /** Clique no botão clip → abre seletor de arquivo */
    const handleClipClick = useCallback(() => {
      inputRef.current?.click();
    }, []);

    /** Input file onChange */
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        processarArquivos(files);
        // Reset o input para permitir selecionar o mesmo arquivo de novo
        e.target.value = '';
      },
      [processarArquivos],
    );

    /** Drag-and-drop */
    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
      setDragOver(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        processarArquivos(files);
      },
      [processarArquivos],
    );

    /** Remove um anexo da lista */
    const removerAnexo = useCallback(
      (id: string) => {
        onAttachmentsChange(attachments.filter((a) => a.id !== id));
        setErro(null);
      },
      [attachments, onAttachmentsChange],
    );

    /** Formata tamanho em KB ou MB */
    function formatarTamanho(bytes: number): string {
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
    }

    /** Gera URL de preview para imagem */
    function previewUrl(att: AttachmentFile): string | null {
      if (att.mediaType.startsWith('image/')) {
        return `data:${att.mediaType};base64,${att.base64}`;
      }
      return null;
    }

    // Drag overlay — estado "ativo" (drag em curso) → glow ciano permitido
    const dragOverlayStyle: React.CSSProperties = dragOver
      ? {
          position: 'absolute',
          inset: 0,
          background: 'rgba(124,124,248,0.06)',
          border: '2px dashed var(--violet)',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          color: 'var(--ink-light)',
          textShadow: 'none',
          pointerEvents: 'none',
        }
      : { display: 'none' };

    return (
      <div
        ref={ref}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: 'relative' }}
      >
        {/* Overlay de drag-and-drop */}
        <div style={dragOverlayStyle} aria-hidden="true">
          {t('chat.dragDrop')}
        </div>

        {/* Thumbnails dos anexos */}
        {attachments.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '8px',
              paddingLeft: '2px',
            }}
          >
            {attachments.map((att) => {
              const preview = previewUrl(att);
              return (
                <div
                  key={att.id}
                  style={{
                    position: 'relative',
                    width: '64px',
                    height: '64px',
                    border: '1px solid var(--ink-mute)',
                    background: 'var(--ink-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt={att.filename}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: '12px',
                        color: 'var(--ink-mute)',
                        textAlign: 'center',
                        padding: '4px',
                        lineHeight: 1.3,
                      }}
                    >
                      <div>PDF</div>
                      <div style={{ marginTop: '4px' }}>{formatarTamanho(att.sizeBytes)}</div>
                    </div>
                  )}
                  {/* Botão remover */}
                  <button
                    type="button"
                    onClick={() => removerAnexo(att.id)}
                    aria-label={t('chat.attach.removeFile')}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '16px',
                      height: '16px',
                      background: 'rgba(34,37,50,0.85)',
                      border: 'none',
                      color: 'var(--ink-mute)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '12px',
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Erro inline */}
        {erro && (
          <div
            role="alert"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: '#7C7CF8',
              marginBottom: '6px',
            }}
          >
            ✗ {erro}
          </div>
        )}

        {/* Botão clip */}
        <button
          type="button"
          data-onboarding="attach-clip"
          onClick={handleClipClick}
          disabled={disabled || attachments.length >= MAX_ARQUIVOS}
          aria-label={t('chat.attach')}
          title={t('chat.attach')}
          style={{
            background: 'transparent',
            border: '1px solid rgba(124,124,248,0.30)',
            color:
              disabled || attachments.length >= MAX_ARQUIVOS
                ? 'var(--ink-mute)'
                : 'var(--ink-light)',
            cursor:
              disabled || attachments.length >= MAX_ARQUIVOS ? 'not-allowed' : 'pointer',
            fontSize: '40px',
            lineHeight: 1,
            padding: '8px 14px',
            minWidth: '60px',
            minHeight: '60px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            textShadow: 'none',
            boxShadow: 'none',
            transition: 'color 180ms ease-out, box-shadow 180ms ease-out, text-shadow 180ms ease-out, border-color 180ms ease-out, transform 100ms ease-out',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            if (!disabled && attachments.length < MAX_ARQUIVOS) {
              e.currentTarget.style.borderColor = '#FF6A00';
              e.currentTarget.style.boxShadow = '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && attachments.length < MAX_ARQUIVOS) {
              e.currentTarget.style.borderColor = 'rgba(124,124,248,0.30)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.color = 'var(--ink-light)';
              e.currentTarget.style.transform = 'none';
            }
          }}
        >
          ⎘
        </button>

        {/* Input file oculto */}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={TIPOS_ACEITOS.join(',')}
          onChange={handleInputChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
      </div>
    );
  },
);

AttachmentClip.displayName = 'AttachmentClip';
